import { cn } from '@/lib/cn';

/**
 * Logo CoreLTB Builders — uzywa oficjalnego pliku webp.
 *
 * Warianty kolorystyczne (oba w public/images/):
 * - logo-white-sm.webp — biały podpis "BUILDERS" + zlota ikonka. Dla DARK theme (cala app).
 *   Tekst "CoreLTB" w pliku jest bialy → na bialym tle podgladu niewidoczny,
 *   na naszym ciemnym tle widoczny.
 * - logo-black-sm.webp — czarny podpis "CoreLTB BUILDERS". Dla LIGHT theme
 *   (przyszly export PDF / oferty handlowe na bialym tle).
 *
 * Wariant rozmiarowy:
 * - compact — sama ikonka (do collapsed sidebar / mini header)
 * - full — ikonka + tekst "CoreLTB Builders" obok (dla rozszerzonego sidebara,
 *   splash screens — ladnie sie czyta nawet z malej ikony)
 */

export interface LogoProps {
  variant?: 'full' | 'compact';
  /** 'auto' (domyslnie) — wersja wg motywu aplikacji; 'dark'/'light' wymusza plik. */
  theme?: 'auto' | 'dark' | 'light';
  className?: string;
}

const LOGO_ON_DARK = '/images/logo-white-sm.webp';
const LOGO_ON_LIGHT = '/images/logo-black-sm.webp';

export function Logo({ variant = 'full', theme = 'auto', className }: LogoProps) {
  return (
    <div
      className={cn('inline-flex items-center gap-2.5', className)}
      aria-label="CoreLTB Builders"
    >
      {/* Oba pliki w DOM, widocznosc przez klase .dark — bez stanu motywu w JS. */}
      {theme !== 'dark' && (
        <LogoImage src={LOGO_ON_LIGHT} className={theme === 'auto' ? 'dark:hidden' : undefined} />
      )}
      {theme !== 'light' && (
        <LogoImage
          src={LOGO_ON_DARK}
          className={theme === 'auto' ? 'hidden dark:block' : undefined}
        />
      )}
      {variant === 'full' && (
        <div className="flex flex-col leading-none">
          <span className="text-base font-semibold tracking-tight text-fg">CoreLTB</span>
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-fg-muted">
            Builders
          </span>
        </div>
      )}
    </div>
  );
}

function LogoImage({ src, className }: { src: string; className?: string }) {
  return (
    <img
      src={src}
      alt="CoreLTB Builders"
      className={cn('h-9 w-auto shrink-0 object-contain', className)}
      width={36}
      height={36}
    />
  );
}

/**
 * Sama ikonka jako <img> — uzywana np. w favicon-like miejscach.
 */
export function LogoMark({
  className,
  theme = 'dark',
}: {
  className?: string;
  theme?: 'dark' | 'light';
}) {
  const src = theme === 'light' ? '/images/logo-black-sm.webp' : '/images/logo-white-sm.webp';
  return (
    <img
      src={src}
      alt="CoreLTB"
      className={cn('h-8 w-8 shrink-0 object-contain', className)}
      width={32}
      height={32}
    />
  );
}
